import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError, setAccessToken, getAccessToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not logged, object = logged
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    if (!getAccessToken()) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setAccessToken(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (identifier, password) => {
    try {
      const { data } = await api.post("/auth/login", { identifier, password });
      if (data.access_token) setAccessToken(data.access_token);
      setUser(data);
      return { ok: true };
    } catch (e) {
      const status = e.response?.status;
      return { ok: false, error: formatApiError(e.response?.data?.detail, status) || e.message };
    }
  };

  const register = async (email, password, name, username) => {
    try {
      const body = { email, password, name };
      if (username) body.username = username;
      await api.post("/auth/register", body);
      // Il nuovo utente NON viene loggato automaticamente: deve aspettare l'approvazione admin
      setAccessToken(null);
      setUser(false);
      return {
        ok: true,
        pendingApproval: true,
        message: "Registrazione completata. Il tuo account è in attesa di approvazione da parte di un amministratore.",
      };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    setAccessToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
